import React, { useMemo, useRef } from 'react';
import { PageLayout } from '@Local/Components/Layout';
import { OverviewListLayout } from '@Local/Components/OverviewList';
import { Grid } from '@mui/material';
import { DateField, MenuItem, SelectField } from '@curves/ui-base';

import { getDeviceRentalBatchPaymentRowLabel } from '@Local/DeviceRental/Components/DeviceRentalBatchPaymentTemplate';
import { parseDate } from '@Util/moment.util';
import useOverview from '@Util/hook/useOverview';
import useDeviceOrder from '@Apis/useDeviceOrderApi';
import useOptions from '@Util/hook/useOptions';
import DeviceRentalDialogBox from '@Local/DeviceRental/Components/DeviceRentalDialogBox';
import { refIsRequiredError } from '@Util/utils';
/**
 * 預設搜尋參數
 * @type {Object}
 */
const defaultSearchParams = {
    status: 99,
    settleMonth: parseDate(new Date()),
    pageIndex: 1,
    pageSize: 50,
}
/**
 * 設備租賃批次付款頁面
 *
 * 功能：
 * - 顯示設備租賃訂單的批次付款任務
 * - 提供狀態和月份篩選功能
 * - 支援分頁瀏覽和操作選單
 *
 * @component
 * @returns {JSX.Element} 設備租賃批次付款頁面
 */
const DeviceRentalBatchPaymentPage = () => {
    const { getDeviceOrderTasksOverviewApi, getDeviceOrderTasksOverviewOptionsApi } = useDeviceOrder();
    const { options } = useOptions({
        getOptionsApi: getDeviceOrderTasksOverviewOptionsApi,
        isFetchOnMount: true,
    });
    const { overviewData, searchParams, getOverviewData, updateOverviewPage } = useOverview({
        getOverviewApi: getDeviceOrderTasksOverviewApi,
        defaultSearchParams: defaultSearchParams,
        isFetchOnMount: true,
    });
    const statusRef = useRef(null);
    const settleMonthRef = useRef(null);
    const refreshRef = useRef(getOverviewData);
    const dialogRef = useRef(null);
    /**
     * 處理搜尋表單提交
     * @param {Event} e - 表單提交事件
     */
    const handleSubmit = (e) => {
        const isError = refIsRequiredError(statusRef);
        if (!isError) {
            const params = {
                ...defaultSearchParams,
                status: statusRef.current.getResult(),
                settleMonth: parseDate(settleMonthRef.current.getResult()),
            };
            getOverviewData(params);
        }
    };
    /**
     * 處理分頁變更
     * @param {Event} e - 分頁變更事件
     * @param {number} page - 目標頁面
     */
    const handlePageChange = (e, page) => {
        updateOverviewPage(page);
    };
    const headerRow = useMemo(
        () => {
            return getDeviceRentalBatchPaymentRowLabel();
        },
        // eslint-disable-next-line
        [],
    );
    return (
        <PageLayout>
            <PageLayout.Header title="設備租賃批次付款" />
            <OverviewListLayout
                contextValue={{
                    dialogRef,
                    refresh: refreshRef.current,
                    setRefresh: (fn) => (refreshRef.current = fn),
                }}
            >
                <OverviewListLayout.SearchBar onSubmit={handleSubmit} isShowActions>
                    <Grid spacing={2} container>
                        <Grid xs={6} item>
                            <SelectField label={'狀態'} key={searchParams?.status} ref={statusRef} defaultValue={searchParams?.status || 99} fullWidth>
                                {Array.isArray(options?.taskStatusOption) &&
                                    options.taskStatusOption.map((item) => {
                                        return (
                                            <MenuItem key={item.value} value={item.value}>
                                                {item.text}
                                            </MenuItem>
                                        );
                                    })}
                            </SelectField>
                        </Grid>
                        <Grid xs={6} item>
                            <DateField
                                label={'月份'}
                                key={searchParams?.settleMonth}
                                ref={settleMonthRef}
                                defaultValue={parseDate(searchParams?.settleMonth)}
                                views={['year', 'month']}
                                openTo={'month'}
                                format={'YYYY/MM'}
                                mask={'____/__'}
                                maxDate={parseDate()}
                                placeholder={'請選擇月份'}
                                fullWidth
                            />
                        </Grid>
                    </Grid>
                </OverviewListLayout.SearchBar>
                <OverviewListLayout.Table
                    headerRow={headerRow}
                    list={overviewData?.list || []}
                    tableRowBodyProps={{ dialogRef: dialogRef }}
                    totalPage={overviewData?.pagination?.totalPage}
                    pageIndex={overviewData?.pagination?.pageIndex}
                    refresh={() => getOverviewData(searchParams)}
                    onChangePage={handlePageChange}
                    isPagination
                />
            </OverviewListLayout>
            <DeviceRentalDialogBox ref={dialogRef} />
        </PageLayout>
    );
};
export default DeviceRentalBatchPaymentPage;
